import { ReadonlyURLSearchParams } from 'next/navigation'
import { AppRouterInstance } from '@/types/next.types'
import { Route } from 'next'
import {
  DAYPASS_URL_PARAM,
  EVENTS_CHECKOUT_BASE_PATH,
  EVENTS_SLUG_POSTFIX,
  EXHIBITION_SLUG_POSTFIX,
  PREVIEW_ROUTES,
  PRIMARY_ADMISSION_EVENT_SLUG,
  TIMED_TICKET_URL_PARAM,
} from '@/config/navigation.config'
import { INTERNAL_REDIRECT_PARAM_KEY } from '@/config/auth.config'
import { PROMO_CODES_URL_PARAM } from '@/config/promo-codes.config'

type AUTH_ROUTES = 'login' | 'logout'

/**
 * Wrapper for window.location
 * Helps with jest testing
 * @returns {Location}
 */
export const getLocation = (): Location => (
  window.location
)

/**
 * Remove every value of a search param from the current URL in place.
 * Reads the live URL and uses `history.replaceState`, which Next syncs with `useSearchParams`,
 * so there's no navigation or new history entry.
 * @param {string} key - the search param to remove
 */
export const removeSearchParamFromUrl = (key: string): void => {
  const url = new URL(window.location.href)
  if (!url.searchParams.has(key)) return

  url.searchParams.delete(key)
  window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`)
}

/**
 * Get the URL for the specified authentication route.
 *
 * @param {AUTH_ROUTES} slug - The type of authentication route ('login' or 'logout').
 * @returns {string} The URL of the specified authentication route.
 */
export const getAuthUrl = (slug: AUTH_ROUTES): string => (
  `/auth/${slug}/`
)

/**
 * Get the login URL with a return_to parameter.
 *
 * @param {string} returnTo - The URL to return to after login.
 * @returns {string} The login URL with the returnTo parameter included.
 */
export const getLoginRoute = (returnTo: string): string => (
  `${getAuthUrl('login')}?returnTo=${returnTo}`
)

/**
 * Get the logout URL with a return_to parameter.
 *
 * @param {string} returnTo - The URL to return to after logout.
 * @returns {string} The logout URL with the return_to parameter included.
 */
export const getLogoutRoute = (returnTo: string): string => (
  `${getAuthUrl('logout')}?${INTERNAL_REDIRECT_PARAM_KEY}=${returnTo}`
)

/**
 * Determines the URL path to return to based on the current site path or
 * location settings.
 *
 * @param {string} sitePath - The site path used to determine the appropriate
 *                            return URL, e.g. `santa-fe/admission`, or `/`
 *                            outside a seller site.
 * @returns {string} The computed "return to" URL path based on the provided
 *                   site path and current location. It may include additional
 *                   path segments in specific contexts.
 */
export const getInternalRedirectPathname = (sitePath: string): string => {
  const { pathname } = getLocation()

  if (pathname.indexOf(PREVIEW_ROUTES.EventsPreview) > -1) {
    return '/'
  }

  if (sitePath === '/') {
    return '/'
  }

  if (pathname.includes(`${EXHIBITION_SLUG_POSTFIX}`)) {
    return `/${sitePath}/${PRIMARY_ADMISSION_EVENT_SLUG}/`
  }

  if (pathname.includes(`/${EVENTS_CHECKOUT_BASE_PATH}`)) {
    return `/${sitePath}/`
  }

  return pathname
}

/**
 * Get admission type from input as a typed response.
 * Returns an empty string when the param is missing or unrecognised. Coercing an absent param to
 * timed_ticket made every page load look like a deep link asking to switch flows, which fired a
 * phantom switch on remount and kicked day pass users back to timed ticketing.
 * @param admissionType
 */
export const getAdmissionType = (admissionType: string | null): AdmissionTypeUrlParam | '' => (
  (
    admissionType === DAYPASS_URL_PARAM
  || admissionType === TIMED_TICKET_URL_PARAM
  )
    ? admissionType
    : ''
)

/**
 * Get incoming date, time, promoCode, and/or search data from
 * url parameters
 * @param {ParsedUrlQuery} query
 * @returns {IncomingUrlData}
 */
export const handleIncomingQueries = (query: ReadonlyURLSearchParams | null): IncomingUrlData => {
  const getQueryValue = (key: string): string => query?.get(key) || ''

  const promoCodes = query?.getAll(PROMO_CODES_URL_PARAM) || []

  return {
    promoCodes,
    date: getQueryValue('date'),
    time: getQueryValue('time'),
    search: getQueryValue('q'),
    category: getQueryValue('category'),
    // eslint-disable-next-line camelcase
    payment_intent_client_secret: getQueryValue('payment_intent_client_secret'),
    // eslint-disable-next-line camelcase
    cart_id: getQueryValue('cart_id'),
    iid: getQueryValue('iid'),
    skip: getQueryValue('skip'),
    // eslint-disable-next-line camelcase
    admission_type: getAdmissionType(query?.get('admission_type') || null),
  }
}

/**
 * Navigate to the purchase confirmation page
 * @param router
 * @param sitePath
 * @param cartId
 * @param {() => void} callback - function fired after the navigation
 * @param {boolean} replace - when true, replace the current history entry instead of pushing a new
 *   one. Set this only when navigating away from the payment-return route so the back button can't
 *   return the customer there (which would re-process a consumed PaymentIntent). Payment-return
 *   should only ever be reachable programmatically. For the normal checkout flow we push, preserving
 *   the customer's history.
 * @returns Promise<boolean>
 */
export const routeToPurchaseConfirmation = (
  router: AppRouterInstance,
  sitePath: string,
  cartId: string,
  callback?: () => void,
  replace = false,
): void => {
  const path = `/${sitePath}/confirmation/${cartId}` as Route
  if (replace) {
    router.replace(path)
  } else {
    router.push(path)
  }
  callback?.()
}

/**
 * Determines whether the given string ends with a trailing slash.
 *
 * This function checks if the last character of the provided string
 * is a forward slash ('/'). It evaluates to `true` if the string has
 * a trailing slash, and `false` otherwise.
 *
 * @param {string} str - The string to check for a trailing slash.
 * @returns {boolean} - `true` if the string ends with a trailing slash, otherwise `false`.
 */
export const hasTrailingSlash = (str: string): boolean => str.lastIndexOf('/') === str.length - 1

/**
 * Checks if the given string starts with a preceding slash ('/').
 *
 * This utility function determines whether the first character
 * of the input string is a forward slash. It can be useful
 * when working with file paths, URLs, or other string values
 * requiring validation of leading slashes.
 *
 * @param {string} str - The input string to check.
 * @returns {boolean} - Returns true if the string starts with a
 *                      forward slash, otherwise false.
 */
export const hasPrecedingSlash = (str: string): boolean => str.indexOf('/') === 0

/**
 * Add both preceding and trailing slashes to urls
 * @param {string} url - any incoming url
 * @returns {string} formatted url
 */
export const formatRelativeUrl = (url: string): string => {
  if (
    // if url starts with http and has a trailing slash
    (url.startsWith('http') && hasTrailingSlash(url))
    // if a relative url with both preceding and trailing slashes
    || (hasPrecedingSlash(url) && hasTrailingSlash(url))
  ) {
    return url
  }

  let nextUrl = url
  // append trailing slash, if not already present and not containing a query string
  nextUrl = (hasTrailingSlash(nextUrl) || nextUrl.indexOf('?') !== -1) ? nextUrl : `${nextUrl}/`
  // prepend trailing slash
  nextUrl = url.startsWith('http') || hasPrecedingSlash(nextUrl) ? nextUrl : `/${nextUrl}`

  return nextUrl
}

/**
 * Build a google maps url
 * The first replace call replaces all whitespace characters with a `+` character.
 * The second replace call replaces all commas with a `%2C` character.
 * https://developers.google.com/maps/documentation/urls/get-started#forming-the-search-url
 * @param {string} venueAddress
 * @returns {string}
 */
export const buildMapUrl = (venueAddress: string): string => (
  `https://www.google.com/maps/search/?api=1&query=${(venueAddress.replace(/\s+/g, '+')).replace(/,/g, '%2C')}`
)

/**
 * The seller's primary admission event page, e.g. `/santa-fe/admission/timed-tickets/`.
 *
 * @param {Transformed.Seller} seller - The seller; its `urlSlug` is the first path segment.
 * @returns {string} The admission URL, with a trailing slash.
 */
const getAdmissionUrlFromSeller = (seller: Transformed.Seller): string => (
  `/${seller.urlSlug}/${EXHIBITION_SLUG_POSTFIX}/${PRIMARY_ADMISSION_EVENT_SLUG}/`
)

/**
 * The seller's events listing page, e.g. `/santa-fe/events/`.
 *
 * @param {Transformed.Seller} seller - The seller; its `urlSlug` is the first path segment.
 * @returns {string} The events URL, with a trailing slash.
 */
const getEventsUrlFromSeller = (seller: Transformed.Seller): string => (
  `/${seller.urlSlug}/${EVENTS_SLUG_POSTFIX}/`
)

/**
 * The seller's landing page for its type: the events listing for an events
 * seller, the primary admission event otherwise.
 *
 * @param {Transformed.Seller} seller - The seller.
 * @returns {string} The seller's primary URL.
 * @throws {Error} When the seller has no `urlSlug` (neither `slr:tkt:url_slug` nor a fallback),
 * rather than building a broken `//admission/...` path.
 */
export const getPrimaryUrlFromSeller = (seller: Transformed.Seller): string => {
  if (!seller.urlSlug) {
    throw new Error(`Seller ${seller.slug} has no url slug`)
  }

  if (seller.type === 'events') {
    return getEventsUrlFromSeller(seller)
  }

  return getAdmissionUrlFromSeller(seller)
}
